import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useAppMessage, useDailyEvent, useDaily, useLocalSessionId, useParticipantIds, useParticipantProperty, usePermissions } from "./callTransport"
import { LOWER_HAND_MESSAGE_TYPE, findParticipant, participantDisplayName, readHandRaisedAt, type LowerHandAppMessage } from "./callSignals"

export type RaisedHandsState = {
  /** Session ids with a raised hand, oldest first (the order Prebuilt numbers them in). */
  raisedIds: string[]
  localRaised: boolean
  /** 1-based queue position, or null when the hand is down. */
  positionOf: (sessionId: string) => number | null
  toggleHand: () => void
  lowerHand: () => void
  /** Hosts can lower someone else's hand; the request travels as an app message the owner verifies. */
  lowerParticipantHand: (sessionId: string) => void
  lowerAllHands: () => void
  canLowerOthers: boolean
}

const noop = () => {}
const EMPTY_STATE: RaisedHandsState = {
  raisedIds: [],
  localRaised: false,
  positionOf: () => null,
  toggleHand: noop,
  lowerHand: noop,
  lowerParticipantHand: noop,
  lowerAllHands: noop,
  canLowerOthers: false,
}

const RaisedHandsContext = createContext<RaisedHandsState>(EMPTY_STATE)
export const RaisedHandsProvider = RaisedHandsContext.Provider
export const useRaisedHandsContext = () => useContext(RaisedHandsContext)

type UseRaisedHandsArgs = {
  enabled: boolean
  /** Called when someone else raises their hand, with their display name (Prebuilt toasts "X raised their hand"). */
  onRemoteHandRaised?: (name: string) => void
}

const byRaisedAt = (a: any, b: any): -1 | 0 | 1 => {
  const left = readHandRaisedAt(a?.userData) ?? 0
  const right = readHandRaisedAt(b?.userData) ?? 0
  return left < right ? -1 : left > right ? 1 : 0
}
const HAND_STATE = "wisefriend.call.hand-state";
const HAND_SYNC = "wisefriend.call.hand-sync";
type HandState = { raisedAt: number | null; revision: number };
const readOwnHand = (value: unknown, sessionId: string): HandState => {
  const data = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  if (data.handRaisedSessionId && data.handRaisedSessionId !== sessionId) return { raisedAt: null, revision: 0 };
  const revision = data.handRaisedRevision;
  return {
    raisedAt: readHandRaisedAt(data),
    revision: typeof revision === 'number' && Number.isSafeInteger(revision) && revision >= 0 ? revision : 0,
  };
};
type HandMessage = LowerHandAppMessage | { type: typeof HAND_STATE; raisedAt: number | null; revision: number } | { type: typeof HAND_SYNC };

/**
 * Raise-hand parity with Daily Prebuilt: the local hand lives in Daily user data (so it is
 * visible to everyone, including people who join later), tiles and the People tab show a
 * numbered hand, and hosts can lower one or all hands.
 */
export function useRaisedHands({ enabled, onRemoteHandRaised }: UseRaisedHandsArgs): RaisedHandsState {
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const localUserData = useParticipantProperty(localSessionId, "userData")
  const { canAdminParticipants } = usePermissions()
  const participantIds = useParticipantIds();
  const metadataRaisedIds = useParticipantIds({ filter: participant => readHandRaisedAt(participant.userData) !== null, sort: byRaisedAt });
  const [signals, setSignals] = useState<Record<string, HandState>>({});
  const ownHand = useRef<HandState>();
  const synchronizedPeers = useRef(new Set<string>());
  const wasJoined = useRef(false);
  const sessionScope = useRef<{ daily: typeof daily; localSessionId: string }>();
  const [joinRevision, setJoinRevision] = useState(0);
  const localRaised = (ownHand.current ?? readOwnHand(localUserData, localSessionId)).raisedAt !== null;
  const raisedIds = useMemo(() => {
    const raised = participantIds.filter(id => id === localSessionId ? localRaised : signals[id] ? signals[id].raisedAt !== null : metadataRaisedIds.includes(id));
    return raised.sort((a, b) => {
      const at = signals[a]?.raisedAt ?? readHandRaisedAt(findParticipant(daily, a)?.userData) ?? 0;
      const bt = signals[b]?.raisedAt ?? readHandRaisedAt(findParticipant(daily, b)?.userData) ?? 0;
      return at - bt || a.localeCompare(b);
    });
  }, [participantIds, signals, metadataRaisedIds, daily, localSessionId, localRaised]);
  const sendRef = useRef<(data: HandMessage, to?: string) => void>(() => undefined);
  // Daily leaves the old room before React clears its local participant. Never
  // treat a retained session id or enabled UI as permission to send during that gap.
  const isJoined = useCallback(() => Boolean(enabled && daily?.meetingState() === 'joined-meeting'), [daily, enabled]);
  const joined = isJoined();
  const send = useCallback((data: HandMessage, to = '*') => {
    if (!isJoined()) return false;
    sendRef.current(data, to);
    return true;
  }, [isJoined]);
  const currentHand = useCallback(() => ownHand.current ?? readOwnHand(localUserData, localSessionId), [localUserData, localSessionId]);

  const setLocalHand = useCallback((raised: boolean) => {
    if (!daily || !isJoined()) return
    const current = localUserData && typeof localUserData === "object" ? (localUserData as Record<string, unknown>) : {}
    const raisedAt = raised ? Date.now() : null;
    const next = { raisedAt, revision: currentHand().revision + 1 };
    if (!Number.isSafeInteger(next.revision)) return;
    ownHand.current = next;
    // Local provider metadata survives a controller remount on the same peer.
    // The session tag prevents a retained browser userData object from seeding
    // a different actual Daily peer after a room transfer.
    void daily.setUserData({ ...current, handRaisedAt: raisedAt, handRaisedRevision: next.revision, handRaisedSessionId: localSessionId });
    setSignals(previous => ({ ...previous, [localSessionId]: next }));
    send({ type: HAND_STATE, ...next }, '*');
  }, [daily, isJoined, localUserData, localSessionId, send, currentHand])

  const lowerHand = useCallback(() => setLocalHand(false), [setLocalHand])
  const toggleHand = useCallback(() => setLocalHand(!localRaised), [localRaised, setLocalHand])

  const lowerHandRef = useRef(lowerHand)
  lowerHandRef.current = lowerHand
  const handleAppMessage = useCallback((event: { data?: HandMessage; fromId?: string }) => {
    const sender = findParticipant(daily, event.fromId);
    if (!isJoined() || !sender || sender.local) return;
    const data = event.data;
    if (data?.type === LOWER_HAND_MESSAGE_TYPE) {
      if (sender.owner) lowerHandRef.current();
    } else if (data?.type === HAND_SYNC) {
      send({ type: HAND_STATE, ...currentHand() }, sender.session_id);
    } else if (data?.type === HAND_STATE && Number.isSafeInteger(data.revision) && data.revision >= 0 &&
      (data.raisedAt === null || (Number.isFinite(data.raisedAt) && data.raisedAt >= 0))) {
      setSignals(previous => previous[sender.session_id] && previous[sender.session_id].revision >= data.revision ? previous :
        { ...previous, [sender.session_id]: { raisedAt: data.raisedAt, revision: data.revision } });
    }
  }, [daily, isJoined, currentHand, send]);
  const sendAppMessage = useAppMessage<HandMessage>({ onAppMessage: handleAppMessage });
  sendRef.current = sendAppMessage;
  useEffect(() => {
    // Native clients are generation-scoped; browser room transfers replace the
    // local session id. A temporarily absent id during verification must not
    // rewind the same peer's revision when its metadata returns.
    if (sessionScope.current && sessionScope.current.daily === daily && (!localSessionId || sessionScope.current.localSessionId === localSessionId)) return;
    sessionScope.current = { daily, localSessionId };
    setSignals({}); ownHand.current = undefined;
    synchronizedPeers.current.clear(); wasJoined.current = false;
  }, [daily, localSessionId]);
  // Events can precede React's roster commit (and Android's joined participant
  // is in event.data). Reconcile the authoritative roster after commit instead
  // of trusting that one event or an early request/reply was observable.
  useEffect(() => {
    const peers = new Set(participantIds.filter(id => id !== localSessionId && findParticipant(daily, id)));
    synchronizedPeers.current.forEach(id => { if (!peers.has(id)) synchronizedPeers.current.delete(id); });
    setSignals(previous => {
      const retained = Object.entries(previous).filter(([id]) => participantIds.includes(id));
      return retained.length === Object.keys(previous).length ? previous : Object.fromEntries(retained);
    });
    if (!joined || !isJoined()) {
      synchronizedPeers.current.clear(); wasJoined.current = false;
      return;
    }
    if (!wasJoined.current) wasJoined.current = send({ type: HAND_SYNC }, '*');
    peers.forEach(id => {
      if (synchronizedPeers.current.has(id)) return;
      // Replay only our own state, including explicit down, with its original
      // timestamp/revision. A sync request elicits state, never another sync.
      if (send({ type: HAND_STATE, ...currentHand() }, id) && send({ type: HAND_SYNC }, id)) {
        synchronizedPeers.current.add(id);
      }
    });
  }, [daily, localSessionId, participantIds, isJoined, joined, send, currentHand, joinRevision]);
  // Joining can finish without another roster update. Wake reconciliation even
  // if an earlier request or response was discarded while either peer joined.
  useDailyEvent('joined-meeting', useCallback(() => {
    synchronizedPeers.current.clear(); wasJoined.current = false;
    setJoinRevision(value => value + 1);
  }, []));

  const lowerParticipantHand = useCallback((sessionId: string) => {
    if (sessionId === localSessionId) {
      lowerHand()
      return
    }
    if (!canAdminParticipants) return
    send({ type: LOWER_HAND_MESSAGE_TYPE }, sessionId)
  }, [canAdminParticipants, localSessionId, lowerHand, send])

  const lowerAllHands = useCallback(() => {
    if (localRaised) lowerHand()
    if (canAdminParticipants && raisedIds.some((id) => id !== localSessionId)) {
      send({ type: LOWER_HAND_MESSAGE_TYPE }, "*")
    }
  }, [canAdminParticipants, localRaised, localSessionId, lowerHand, raisedIds, send])

  // Announce newly raised remote hands once each.
  const previousRaisedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const previous = previousRaisedRef.current
    const next = new Set(raisedIds)
    if (enabled && onRemoteHandRaised) {
      raisedIds.forEach((id) => {
        if (id !== localSessionId && !previous.has(id)) {
          onRemoteHandRaised(participantDisplayName(daily, id, "Someone"))
        }
      })
    }
    previousRaisedRef.current = next
  }, [daily, enabled, localSessionId, onRemoteHandRaised, raisedIds])

  const positionOf = useCallback((sessionId: string) => {
    const index = raisedIds.indexOf(sessionId)
    return index === -1 ? null : index + 1
  }, [raisedIds])

  return useMemo(() => ({
    raisedIds,
    localRaised,
    positionOf,
    toggleHand,
    lowerHand,
    lowerParticipantHand,
    lowerAllHands,
    canLowerOthers: Boolean(canAdminParticipants),
  }), [canAdminParticipants, localRaised, lowerAllHands, lowerHand, lowerParticipantHand, positionOf, raisedIds, toggleHand])
}
